import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { styled } from '@mui/material/styles';
import { groupDataByTimeRange } from '../../utils/chartUtils';

const StyledCard = styled(Card)(({ theme }) => ({
  background: '#fff',
  border: '0 solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

const SummaryCard = styled(Card)(({ theme, severity, isSelected }) => {
  const getSeverityColors = () => {
    switch (severity) {
      case 'low':
        return {
          bg: isSelected ? '#EFF6FF' : '#fff',
          border: isSelected ? '2px solid #2563EB' : '0 solid #e2e8f0',
          dot: isSelected ? '#2563EB' : '#2563EB',
        };
      case 'medium':
        return {
          bg: isSelected ? '#FFFBEB' : '#fff',
          border: isSelected ? '2px solid #F59E0B' : '1px solid #e2e8f0',
          dot: isSelected ? '#F59E0B' : '#ea580c',
        };
      case 'critical':
        return {
          bg: isSelected ? '#FEF2F2' : '#fff',
          border: isSelected ? '2px solid #DC2626' : '0 solid #e2e8f0',
          dot: isSelected ? '#DC2626' : '#DC2626',
        };
      default:
        return {
          bg: '#fff',
          border: '0 solid #e2e8f0',
          dot: '#fff',
        };
    }
  };

  const colors = getSeverityColors();

  return {
    background: colors.bg,
    border: colors.border,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'start',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '35px',
      right: '12px',
      width: '0.75rem',
      height: '0.75rem',
      borderRadius: '50%',
      backgroundColor: colors.dot,
    },
  };
});

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
}));

const IssueDetectionTrends = ({ issueTrends, filters }) => {
  const [selectedSeverity, setSelectedSeverity] = useState(null);

  // Format date range for display
  const formatDateRange = (range) => {
    switch (range) {
      case '7d':
        return 'Last 7 days';
      case '30d':
        return 'Last 30 days';
      case '90d':
        return 'Last 90 days';
      case '1y':
        return 'Last year';
      default:
        return 'Last 7 days';
    }
  };

  // Calculate issue counts by severity
  const issueCounts = useMemo(() => {
    const criticalCount = (issueTrends?.critical || []).reduce((sum, item) => sum + item.count, 0);
    const mediumCount = (issueTrends?.medium || []).reduce((sum, item) => sum + item.count, 0);
    const lowCount = (issueTrends?.low || []).reduce((sum, item) => sum + item.count, 0);

    // Use sample data if no real data available
    return {
      critical: criticalCount || 0,
      medium: mediumCount || 0,
      low: lowCount || 0,
    };
  }, [issueTrends]);

  // Format chart data
  const chartData = useMemo(() => {
    // Use real data if available, otherwise return empty array for empty state
    if (!issueTrends || !issueTrends.critical || issueTrends.critical.length === 0) {
      return [];
    }

    const criticalData = groupDataByTimeRange(issueTrends?.critical || [], filters?.dateRange || '30d', 'date', 'count');
    const mediumData = groupDataByTimeRange(issueTrends?.medium || [], filters?.dateRange || '30d', 'date', 'count');
    const lowData = groupDataByTimeRange(issueTrends?.low || [], filters?.dateRange || '30d', 'date', 'count');

    const allKeys = [...new Set([
      ...criticalData.map(d => d.label),
      ...mediumData.map(d => d.label),
      ...lowData.map(d => d.label)
    ])];

    const criticalMap = new Map(criticalData.map(d => [d.label, d.total_value]));
    const mediumMap = new Map(mediumData.map(d => [d.label, d.total_value]));
    const lowMap = new Map(lowData.map(d => [d.label, d.total_value]));

    return allKeys.map(label => ({
      label,
      critical: criticalMap.get(label) || 0,
      medium: mediumMap.get(label) || 0,
      low: lowMap.get(label) || 0,
    }));
  }, [issueTrends, filters?.dateRange]);

  const summaryCards = [
    { label: 'Low Issues', count: issueCounts.low, severity: 'low' },
    { label: 'Medium Issues', count: issueCounts.medium, severity: 'medium' },
    { label: 'Critical Issues', count: issueCounts.critical, severity: 'critical' },
  ];

  return (
      <Grid item xs={12}>
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              mb: 3 
            }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#020817',
                    mb: 1,
                    fontSize: '1.25rem',
                    lineHeight: '1.75rem',
                    letterSpacing: '-0.025em',
                  }}
                >
                  Issue Detection Trends
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    fontWeight: 500,
                    lineHeight: '1.25rem',
                    letterSpacing: '-0.015em',
                    fontSize: '0.875rem'
                  }}
                >
                  Critical insights across development lifecycle
                </Typography>
              </Box>
            </Box>

            <Grid item xs={12}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
                {summaryCards.map((card, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <SummaryCard 
                      severity={card.severity}
                      isSelected={selectedSeverity === card.severity}
                      onClick={() => {
                        setSelectedSeverity(selectedSeverity === card.severity ? null : card.severity);
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: selectedSeverity === card.severity 
                            ? (card.severity === 'low' ? '#2563EB' : card.severity === 'medium' ? '#F59E0B' : '#DC2626')
                            : '#020817',
                          mb: 0.5,
                          fontSize: '1.5rem',
                          lineHeight: '1.75rem',
                          transition: 'color 0.2s ease-in-out',
                        }}
                      >
                        {card.count}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6b7280',
                          fontWeight: 600,
                          lineHeight: '1rem',
                          fontSize: '0.75rem'
                        }}
                      >
                        {card.label}
                      </Typography>
                    </SummaryCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Box
              role="figure"
              aria-label="Area chart showing issue detection trends for critical, medium, and low severities over the selected time period."
              sx={{ height: { xs: 250, sm: 300, md: 350 } }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} aria-hidden="true">
                  <defs>
                    <linearGradient id="colorLow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="colorMedium" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="colorCritical" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC2626" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#DC2626" stopOpacity={0.02} />
                    </linearGradient>

                    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.1)" />
                    </filter>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#E5E7EB"
                    strokeOpacity={0.6}
                    horizontal={true}
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{
                      fill: '#6B7280',
                      fontSize: 12,
                      fontWeight: 500
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                  />

                  <YAxis
                    tick={{
                      fill: '#6B7280',
                      fontSize: 12,
                      fontWeight: 500
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={5}
                    domain={[0, 'dataMax + 10']}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    labelStyle={{
                      color: '#111827',
                      fontWeight: '600',
                      marginBottom: '8px'
                    }}
                    cursor={{ stroke: '#E5E7EB', strokeWidth: 1, strokeDasharray: '5 5' }}
                  />

                  <Area
                    type="monotone"
                    dataKey="low"
                    stackId="1"
                    stroke="#2563EB"
                    fill="url(#colorLow)"
                    strokeWidth={selectedSeverity === 'low' ? 3 : selectedSeverity !== null ? 1 : 1.5}
                    strokeOpacity={selectedSeverity === null || selectedSeverity === 'low' ? 1 : 0.3}
                    fillOpacity={selectedSeverity === null || selectedSeverity === 'low' ? 1 : 0.1}
                    name="Low Priority"
                    filter={selectedSeverity === 'low' ? "url(#shadow)" : undefined}
                  />
                  <Area
                    type="monotone"
                    dataKey="medium"
                    stackId="1"
                    stroke="#F59E0B"
                    fill="url(#colorMedium)"
                    strokeWidth={selectedSeverity === 'medium' ? 3 : selectedSeverity !== null ? 1 : 1.5}
                    strokeOpacity={selectedSeverity === null || selectedSeverity === 'medium' ? 1 : 0.3}
                    fillOpacity={selectedSeverity === null || selectedSeverity === 'medium' ? 1 : 0.1}
                    name="Medium Priority"
                    filter={selectedSeverity === 'medium' ? "url(#shadow)" : undefined}
                  />
                  <Area
                    type="monotone"
                    dataKey="critical"
                    stackId="1"
                    stroke="#DC2626"
                    fill="url(#colorCritical)"
                    strokeWidth={selectedSeverity === 'critical' ? 3 : selectedSeverity !== null ? 1 : 1.5}
                    strokeOpacity={selectedSeverity === null || selectedSeverity === 'critical' ? 1 : 0.3}
                    fillOpacity={selectedSeverity === null || selectedSeverity === 'critical' ? 1 : 0.1}
                    name="Critical Issues"
                    filter={selectedSeverity === 'critical' ? "url(#shadow)" : undefined}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
  );
};

export default IssueDetectionTrends;